/**
 * Forums hub discovery — conversation-first, not a certification directory.
 *
 * Popular cert forums are a curated shortlist (demand / keyword data).
 * Adding more certification forums should update search matching and this
 * shortlist, not grow a tile wall on /forums.
 */

import { canonicalCertSlug, extraCertCommunityTerms, resolveCertSearchName } from "@/lib/cert-slug";

export type PopularForum = {
  key: string;
  /** Chip + compact badge text */
  label: string;
  title: string;
  description: string;
  /** Short tile initials when the label is long */
  badge: string;
  aliases: string[];
  /** Used when no live category matches — /forums/[slug] redirects to the field if needed */
  canonicalSlug: string;
};

export const POPULAR_FORUMS: PopularForum[] = [
  {
    key: "pmp",
    label: "PMP",
    title: "PMP Forum",
    description: "Exam prep, study strategies, and resources",
    badge: "PMP",
    aliases: ["pmp"],
    canonicalSlug: "pmp",
  },
  {
    key: "security-plus",
    label: "Security+",
    title: "CompTIA Security+ Forum",
    description: "Exam prep, study strategies, and resources",
    badge: "Sec+",
    aliases: ["security+", "security-plus", "security plus", "comptia security+", "comptia security plus", "comptia-security-plus", "comptia-security+"],
    canonicalSlug: "comptia-security-plus",
  },
  {
    key: "cissp",
    label: "CISSP",
    title: "CISSP Forum",
    description: "Exam prep, study strategies, and resources",
    badge: "CISSP",
    aliases: ["cissp"],
    canonicalSlug: "cissp",
  },
  {
    key: "aws",
    label: "AWS",
    title: "AWS Solutions Architect Forum",
    description: "Exam prep, study strategies, and resources",
    badge: "AWS",
    aliases: ["aws", "aws solutions architect", "solutions architect"],
    canonicalSlug: "aws-solutions-architect",
  },
  {
    key: "capm",
    label: "CAPM",
    title: "CAPM Forum",
    description: "Exam prep, study strategies, and resources",
    badge: "CAPM",
    aliases: ["capm"],
    canonicalSlug: "capm",
  },
  {
    key: "cpa",
    label: "CPA",
    title: "CPA Forum",
    description: "Exam prep, study strategies, and career advice",
    badge: "CPA",
    aliases: ["cpa"],
    canonicalSlug: "cpa",
  },
  {
    key: "cfa",
    label: "CFA",
    title: "CFA Forum",
    description: "Exam prep, study strategies, and career advice",
    badge: "CFA",
    aliases: ["cfa", "cfa level 1", "cfa level 2", "cfa level 3", "chartered financial analyst"],
    canonicalSlug: "cfa",
  },
];

/**
 * Display label for an exam-scoped forum URL.
 * `/forums/cpa` is a slug, not a name — never show the lowercase path in the heading.
 */
export function formatForumExamLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  const slug = canonicalCertSlug(trimmed);
  const phrase = trimmed.replace(/-/g, " ").trim();
  const phraseLower = phrase.toLowerCase();

  const popular = POPULAR_FORUMS.find((p) => {
    if (p.canonicalSlug === slug || p.key === slug) return true;
    return [p.label, p.title, ...p.aliases].some((a) => {
      const n = a.toLowerCase();
      return n === slug || n === phraseLower || canonicalCertSlug(a) === slug;
    });
  });
  if (popular) return popular.label;

  const resolved = resolveCertSearchName(phraseLower);
  if (resolved !== phraseLower) return resolved;

  const extras = extraCertCommunityTerms(slug);
  if (extras[0]) return extras[0];

  if (/^[a-z]{2,8}$/.test(slug)) return slug.toUpperCase();
  if (/^[a-z0-9]{1,8}(?:-[a-z0-9]{1,8}){1,2}$/.test(slug)) {
    return slug.split("-").map((p) => p.toUpperCase()).join("-");
  }

  return phrase.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export type BrowseCategory = {
  slug: string;
  title: string;
  blurb: string;
};

/** Umbrella fields only — not individual certifications. */
export const BROWSE_CATEGORIES: BrowseCategory[] = [
  {
    slug: "tech",
    title: "Technology",
    blurb: "AWS, CompTIA, Microsoft, Google, ISC2",
  },
  {
    slug: "project-management",
    title: "Project Management",
    blurb: "PMP, CAPM, and PMI credentials",
  },
  {
    slug: "accounting-finance",
    title: "Accounting & Finance",
    blurb: "CPA, CMA, CFA, and finance credentials",
  },
  {
    slug: "medical",
    title: "Medical",
    blurb: "NCLEX, USMLE, MCAT, and healthcare exams",
  },
  {
    slug: "bar-exam",
    title: "Legal",
    blurb: "Bar exam and related legal exams",
  },
];

export const GENERAL_FORUM = {
  slug: "general",
  title: "General Discussion",
  blurb:
    "Careers, study habits, resources, exam experiences, and topics that span certifications.",
};

export type HubCategory = {
  id: string;
  dbId?: string;
  slug: string;
  name: string;
  description?: string | null;
};

export type HubForumMatch = {
  href: string;
  title: string;
  subtitle: string;
  kind: "popular" | "category";
};

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/\+/g, "plus")
    .replace(/[^a-z0-9]+/g, "");
}

function textMatchesQuery(query: string, ...parts: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const nq = normalize(q);
  return parts.some((part) => {
    if (!part) return false;
    const raw = part.toLowerCase();
    return raw.includes(q) || (nq.length >= 2 && normalize(part).includes(nq));
  });
}

/** Popular cards link to the exam-scoped URL, not the unlabeled umbrella. */
export function resolvePopularForum(
  popular: PopularForum,
): { href: string; title: string; description: string } {
  return {
    href: `/forums/${popular.canonicalSlug}`,
    title: popular.title,
    description: popular.description,
  };
}

export function searchHubForums(
  query: string,
  categories: HubCategory[],
): HubForumMatch[] {
  const q = query.trim();
  if (q.length < 2) return [];

  const seen = new Set<string>();
  const results: HubForumMatch[] = [];

  const push = (item: HubForumMatch) => {
    if (seen.has(item.href)) return;
    seen.add(item.href);
    results.push(item);
  };

  for (const popular of POPULAR_FORUMS) {
    if (
      textMatchesQuery(
        q,
        popular.label,
        popular.title,
        popular.description,
        ...popular.aliases,
      )
    ) {
      const resolved = resolvePopularForum(popular);
      push({
        href: resolved.href,
        title: resolved.title,
        subtitle: resolved.description,
        kind: "popular",
      });
    }
  }

  for (const browse of BROWSE_CATEGORIES) {
    if (textMatchesQuery(q, browse.title, browse.blurb, browse.slug)) {
      push({
        href: `/forums/${browse.slug}`,
        title: browse.title,
        subtitle: browse.blurb,
        kind: "category",
      });
    }
  }

  if (textMatchesQuery(q, GENERAL_FORUM.title, GENERAL_FORUM.blurb, GENERAL_FORUM.slug)) {
    push({
      href: `/forums/${GENERAL_FORUM.slug}`,
      title: GENERAL_FORUM.title,
      subtitle: GENERAL_FORUM.blurb,
      kind: "category",
    });
  }

  return results.slice(0, 8);
}
